import {
  Component,
  QueryList,
  Renderer2,
  ViewChildren,
  AfterViewInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  CcyPairLookupService,
  CcyPairPromiseLookupService,
  MarketTimePromiseLookupService,
  MarketTimeLookupService,
  ProfileSettingKeys,
  ActiveUser,
  ClientPromiseLookupService,
  AccountPromiseLookupService,
  ClientAccountVisibilityType,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { BfxUserSettingsComponent } from "../bfx-user-settings/bfx-user-settings.component";
import { ICPrivateOrderDefaultsComponent } from "../bfx-element-library/ic-private-order-defaults.component";
import { BfxAmountSpinnerComponent } from "../bfx-interface-controls/bfx-amount-spinner.component";
import { BfxInputControlStateEnum } from "../bfx-interface-controls/models/bfx-interface-controls.enums";
import { BfxClientIntentToggleComponent } from "../bfx-interface-controls/bfx-toggle-client-intent.component";
import { OEApiService } from "../order-entry/services/oe-api.service";
import { BfxOrderEntrySettingsComponent } from "./bfx-order-entry-settings.component";
import { OEDefaultsModel } from "./model/bfx-order-entry-defaults.model";
import {
  ICAutocompleteGenericModel,
  ICFixedCcyModel,
} from "../order-entry/interface/controls/models/bfx-interface-controls.models";
import { ICAutocompleteDefaultsComponent } from "../bfx-element-library/ic-autocomplete-defaults.component";
import { ICCcyPairDefaultsComponent } from "../bfx-element-library/ic-ccy-pair-defaults.component";
import { BfxAutoCompleteComponent } from "../bfx-element-library/bfx-autocomplete.component";
import {
  IBfxUserSettingsPageSubComponent,
  BfxUserSettingsValidityState,
} from "./bfx-user-settings-page.component";
import { BfxUserSettingsComponentState } from "./bfx-user-settings.service";
import { ICAlgoTypeDefaultsComponent } from "../bfx-element-library/ic-algo-type-defaults.component";
import { ICFixedCcyComponent } from "../ic-controls/ic-fixed-ccy.component";
import { OENInputControl } from "../order-entry/data/enums/oe-input-control.enum";
import { BfxOrderEntryDefaultsService } from "app/bfx-user-settings/support/bfx-order-entry-defaults.service";

const PROFILE_KEY = ProfileSettingKeys.userOrderEntryAlgoSettings;
const TAB_PROFILE_KEY = ProfileSettingKeys.userOrderEntryActiveTabsSettings;

@Component({
  selector: "bfx-order-entry-settings-algo",
  templateUrl: "./html/bfx-order-entry-settings-algo.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxOrderEntrySettingsAlgoComponent
  implements IBfxUserSettingsPageSubComponent<BfxOrderEntrySettingsComponent>, AfterViewInit
{
  /**********************************************************************/
  /* DATA */

  public dataChanged: boolean = false;
  public parentComponent: BfxUserSettingsComponent = null;
  public activeSubComponent: number = 0;
  public categoryComponent: BfxOrderEntrySettingsComponent = null;
  //
  private entryDataToUpdate: string = "";
  private tabDataToUpdate: string = "";

  /*---------- Interface References ---------- */
  @ViewChildren("inputClient")
  icClient: QueryList<ICAutocompleteDefaultsComponent>;
  @ViewChildren("inputAccount")
  icAccount: QueryList<ICAutocompleteDefaultsComponent>;
  @ViewChildren("inputCcyPair")
  icCcyPair: QueryList<ICCcyPairDefaultsComponent>;
  @ViewChildren("inputAlgoType")
  icAlgoType: QueryList<ICAlgoTypeDefaultsComponent>;
  @ViewChildren("toggleClientIntent")
  icClientIntent: QueryList<BfxClientIntentToggleComponent>;
  @ViewChildren("toggleFixedCcy") icFixedCcy: QueryList<ICFixedCcyComponent>;
  @ViewChildren("inputOrderAmount")
  icOrderAmount: QueryList<BfxAmountSpinnerComponent>;
  @ViewChildren("inputGoodFrom")
  icGoodFrom: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputGoodUntil")
  icGoodUntil: QueryList<BfxAutoCompleteComponent>;
  @ViewChildren("inputPrivateOrder")
  icPrivateOrder: QueryList<ICPrivateOrderDefaultsComponent>;

  public data: OEDefaultsModel = new OEDefaultsModel();
  public loadedData: OEDefaultsModel = new OEDefaultsModel();
  public control: {
    showIntentAndAmount: boolean;
    isLoading: boolean;
    isInitializing: boolean;
    clientAccountConstraintAutoSelect: boolean;
  } = {
    showIntentAndAmount: true,
    isLoading: false,
    isInitializing: true,
    clientAccountConstraintAutoSelect: true,
  };

  /**********************************************************************/
  /* CONSTRUCTOR */

  constructor(
    private translate: TranslateService,
    private api: OEApiService,
    private currencyPairService: CcyPairPromiseLookupService,
    private clientLookup: ClientPromiseLookupService,
    private accountLookup: AccountPromiseLookupService,
    private defaultsService: BfxOrderEntryDefaultsService,
    public renderer2: Renderer2,
    public cd: ChangeDetectorRef,
  ) {
    this.initializeSettings();
    this.initializeLabelTranslations();
    this.initializeDatasets();
  }

  ngAfterViewInit() {}

  /**********************************************************************/
  /* DATASET INITIALIZATION */

  private initializeSettings(): void {
    // more
    let moreSettings = this.api.getSystemConfigForMorePanel();
    this.data.privateOrder.show = this.data.privateOrder.showConfig = moreSettings.showPrivateOrder;
  }

  private initializeLabelTranslations(): void {
    this.data.currencyPair.label = this.translate.instant(this.data.currencyPair.labelKey);
    this.data.clientIntent.label = this.translate.instant(this.data.clientIntent.labelKey);
    this.data.goodFrom.label = this.translate.instant(this.data.goodFrom.labelKey);
    this.data.goodUntil.label = this.translate.instant(this.data.goodUntil.labelKey);
    this.data.goodFrom.tooltip = this.translate.instant(this.data.goodFrom.tooltipKey);
    this.data.goodUntil.tooltip = this.translate.instant(this.data.goodUntil.tooltipKey);
    this.data.privateOrder.label = this.translate.instant(this.data.privateOrder.labelKey);
  }

  public initializeDatasets(): void {
    this.initializeAlgoTypeDataset();
    this.initializeCurrencyPairs();
    this.initializeMarketTimes();
  }

  private initializeCurrencyPairs(): void {
    let dataset = this.api.getAlgoCcyPairsDataset();
    if (dataset != null && dataset instanceof Array && dataset.length > 0) {
      this.data.currencyPair.dataset = Object.assign([], dataset);
    } else {
      logger.out.error(
        "ERROR: algoDefaults.initializeCurrencyPairs(): CcyPairs dataset load failed!",
      );
      this.data.currencyPair.dataset = [];
    }
  }

  private initializeAlgoTypeDataset(): void {
    this.data.algoType.dataset = this.api.loadTradableAlgos();
  }

  private initializeMarketTimes(): void {
    this.defaultsService.populateGoodTimeFieldSettings(this.data.goodFrom, this.data.goodUntil);
  }

  /**********************************************************************/
  /* SETTINGS METHODS */
  /**********************************************************************/

  /* SETTINGS - INITIALIZE */
  public onTabActivated(event: any): void {}

  public initialize(): void {
    let activeUser: ActiveUser = this.parentComponent.activeUser;
    this.activeSubComponent = this.parentComponent.activeSubCategory;
    if (activeUser.profileSettings) {
      if (activeUser.profileSettings.has(PROFILE_KEY)) {
        let savedData = activeUser.profileSettings.get(PROFILE_KEY);
        this.loadData(savedData);
      } else {
        this.control.isInitializing = false;
      }
    } else {
      this.control.isInitializing = false;
    }
  }

  /* SETTINGS - VALIDATE */
  public validate(): Promise<BfxUserSettingsValidityState> {
    return new Promise<BfxUserSettingsValidityState>((resolve) => {
      let state = this.validateData();
      //logger.out.info(`${this.constructor.name}.validate() valid:BfxUserSettingsValidityState.${BfxUserSettingsValidityState[state]}`);
      resolve(state);
    });
  }

  /* SETTINGS - SAVE */
  public save(request: CubiqTS.ProfileUpdateRequestImpl): void {
    this.saveData(request);
  }

  public postSave(request: CubiqTS.ProfileUpdateRequestImpl): void {
    let activeUser = this.parentComponent.activeUser;
    if (activeUser.profileSettings) {
      activeUser.profileSettings.set(PROFILE_KEY, this.entryDataToUpdate);
      activeUser.profileSettings.set(TAB_PROFILE_KEY, this.tabDataToUpdate);
    }
    this.dataChanged = false;
  }

  /* SETTINGS - RESET */
  public reset(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.resetData().then(() => {
        this.cd.detectChanges();
        resolve();
      });
    });
  }

  public postReset(): void {
    this.dataChanged = false;
    this.cd.detectChanges();
  }

  /* SETTINGS - CHANGE */
  private detectChanges(control: OENInputControl): boolean {
    let notLoading: boolean = !this.control.isLoading && !this.control.isInitializing;
    let state: BfxUserSettingsValidityState = this.validateData();
    return (
      notLoading &&
      (state == BfxUserSettingsValidityState.INVALID || this.detectModelChanges(control))
    );
  }

  private detectModelChanges(control: OENInputControl): boolean {
    if (control in OENInputControl) {
      switch (control) {
        case OENInputControl.CLIENT:
          if (
            this.data.client.value instanceof CubiqTS.ClientImpl &&
            this.loadedData.client.value instanceof CubiqTS.ClientImpl
          ) {
            return this.data.client.value.clientId !== this.loadedData.client.value.clientId;
          }
          return this.data.client.value !== this.loadedData.client.value;
        case OENInputControl.ACCOUNT:
          if (
            this.data.account.value instanceof CubiqTS.AccountImpl &&
            this.loadedData.account.value instanceof CubiqTS.AccountImpl
          ) {
            return this.data.account.value.id !== this.loadedData.account.value.id;
          }
          return this.data.account.value !== this.loadedData.account.value;
        case OENInputControl.CCY_PAIR:
          // Match either same string values or both null/undefined
          return this.data.currencyPair.product != this.loadedData.currencyPair.product;
        case OENInputControl.ALGO_TYPE:
          if (
            this.data.algoType.value instanceof ICAutocompleteGenericModel &&
            this.loadedData.algoType.value instanceof ICAutocompleteGenericModel
          ) {
            return this.data.algoType.value.value !== this.loadedData.algoType.value.value;
          }
          return this.data.algoType.value !== this.loadedData.algoType.value;
        case OENInputControl.CLIENT_INTENT:
          if (!this.control.showIntentAndAmount) {
            return false;
          } else if (this.data.clientIntent.value == null) {
            return true;
          } else if (
            this.data.clientIntent.value != null &&
            this.loadedData.clientIntent.value != null
          ) {
            return this.data.clientIntent.value !== this.loadedData.clientIntent.value;
          }
          return true;
        case OENInputControl.FIXED_CCY:
          if (
            !this.control.showIntentAndAmount ||
            typeof this.data.currencyPair.product !== "string"
          ) {
            return false;
          } else if (this.data.fixedCcy.value == null) {
            return true;
          } else if (
            this.data.fixedCcy.value instanceof ICFixedCcyModel &&
            this.loadedData.fixedCcy.value instanceof ICFixedCcyModel
          ) {
            return this.data.fixedCcy.value.ccy !== this.loadedData.fixedCcy.value.ccy;
          }
          return true;
        case OENInputControl.ORDER_AMOUNT:
          if (
            this.data.orderAmount.value instanceof CubiqTS.BigNumber &&
            this.loadedData.orderAmount.value instanceof CubiqTS.BigNumber
          ) {
            return !(this.data.orderAmount.value as CubiqTS.BigNumber).isEqualTo(
              this.loadedData.orderAmount.value,
            );
          }
          return this.data.orderAmount.value !== this.loadedData.orderAmount.value;
        case OENInputControl.GOOD_FROM:
          if (
            this.data.goodFrom.value instanceof ICAutocompleteGenericModel &&
            this.loadedData.goodFrom.value instanceof ICAutocompleteGenericModel
          ) {
            return this.data.goodFrom.value.value !== this.loadedData.goodFrom.value.value;
          }
          return this.data.goodFrom.value !== this.loadedData.goodFrom.value;
        case OENInputControl.GOOD_UNTIL:
          if (
            this.data.goodUntil.value instanceof ICAutocompleteGenericModel &&
            this.loadedData.goodUntil.value instanceof ICAutocompleteGenericModel
          ) {
            return this.data.goodUntil.value.value !== this.loadedData.goodUntil.value.value;
          }
          return this.data.goodUntil.value !== this.loadedData.goodUntil.value;
        case OENInputControl.PRIVATE_ORDER:
          return this.data.privateOrder.value !== this.loadedData.privateOrder.value;
        default:
          logger.out.warn(
            `WARNING: ${this.constructor.name}.detectChanges() => Unhandled input control ${control}`,
          );
          return true;
      }
    }
    return false;
  }

  private updateLoadedData(): void {
    this.loadedData = new OEDefaultsModel();
    // client
    this.loadedData.client.value =
      this.data.client.value instanceof CubiqTS.ClientImpl
        ? Object.assign(new CubiqTS.ClientImpl(), this.data.client.value)
        : null;
    // account
    this.loadedData.account.value =
      this.data.account.value instanceof CubiqTS.AccountImpl
        ? Object.assign(new CubiqTS.AccountImpl(), this.data.account.value)
        : null;
    // ccyPair
    this.loadedData.currencyPair.product = this.data.currencyPair.product;
    // algo type
    this.loadedData.algoType.value =
      this.data.algoType.value instanceof ICAutocompleteGenericModel
        ? Object.assign(new ICAutocompleteGenericModel(), this.data.algoType.value)
        : null;
    // clientIntent
    this.loadedData.clientIntent.value =
      this.control.showIntentAndAmount && this.data.clientIntent.value != null
        ? this.data.clientIntent.value
        : null;
    // ccy
    this.loadedData.fixedCcy.value =
      this.control.showIntentAndAmount && this.data.fixedCcy.value instanceof ICFixedCcyModel
        ? Object.assign(new ICFixedCcyModel(), this.data.fixedCcy.value)
        : null;
    // orderAmount
    this.loadedData.orderAmount.value =
      this.data.orderAmount.value instanceof CubiqTS.BigNumber
        ? new CubiqTS.BigNumber(this.data.orderAmount.value)
        : null;
    // goodFrom
    this.loadedData.goodFrom.value =
      this.data.goodFrom.value instanceof ICAutocompleteGenericModel
        ? Object.assign(new ICAutocompleteGenericModel(), this.data.goodFrom.value)
        : null;
    // goodUntil
    this.loadedData.goodUntil.value =
      this.data.goodUntil.value instanceof ICAutocompleteGenericModel
        ? Object.assign(new ICAutocompleteGenericModel(), this.data.goodUntil.value)
        : null;
    //private order
    this.loadedData.privateOrder.value = this.data.privateOrder.value;
  }

  public pageDataChanged() {
    if (!this.control.isLoading && !this.control.isInitializing) {
      // register changes after loading is finished
      this.dataChanged = true;
      this.parentComponent.validate(this.categoryComponent);
    }
  }

  public hasDataChanged(): boolean {
    return this.dataChanged;
  }

  public onComponentStateChanged(componentState: BfxUserSettingsComponentState): void {}

  public onDataChanged(): void {}

  /**********************************************************************/
  /* SETTINGS METHODS - HELPERS */

  private getProfileSettingFromKey(
    profileSettingKey: string,
    request: CubiqTS.ProfileUpdateRequestImpl,
  ): CubiqTS.ProfileSettingImpl {
    let profileSettings: Array<any> = request.settings;
    if (!profileSettings) return;
    let savedOrderEntryPreferences: CubiqTS.ProfileSettingImpl = profileSettings.find((setting) => {
      return setting.profileSettingKey == profileSettingKey;
    });
    return savedOrderEntryPreferences;
  }

  // Handles Entry Data; Tabs are assumed valid
  private validateData(): BfxUserSettingsValidityState {
    let result: BfxUserSettingsValidityState;
    let checkStateFn = (state: BfxInputControlStateEnum) => {
      if (
        result === BfxUserSettingsValidityState.INVALID ||
        state === BfxInputControlStateEnum.INVALID
      ) {
        result = BfxUserSettingsValidityState.INVALID;
      } else if (
        result === BfxUserSettingsValidityState.VALID ||
        state === BfxInputControlStateEnum.VALID
      ) {
        result = BfxUserSettingsValidityState.VALID;
      } else if (state === BfxInputControlStateEnum.ACTIVE) {
        result = BfxUserSettingsValidityState.ACTIVE_UNSAVEABLE;
      }
    };

    this.icClient.forEach((c) => checkStateFn(c.data.state));
    this.icAccount.forEach((c) => checkStateFn(c.data.state));
    this.icCcyPair.forEach((c) => checkStateFn(c.data.state));
    this.icAlgoType.forEach((c) => checkStateFn(c.data.state));
    this.icClientIntent.forEach((c) => checkStateFn(c.data.state));
    this.icFixedCcy.forEach(() => checkStateFn(BfxInputControlStateEnum.VALID));
    this.icOrderAmount.forEach((c) => checkStateFn(c.state));
    this.icGoodFrom.forEach((c) => checkStateFn(c.state));
    this.icGoodUntil.forEach((c) => checkStateFn(c.state));
    // icPrivateOrder can not be invalid
    // this.icPrivateOrder.forEach(c => result = result && c.data.state !== BfxInputControlStateEnum.INVALID);
    return result === BfxUserSettingsValidityState.INVALID
      ? result
      : this.dataChanged
        ? BfxUserSettingsValidityState.VALID
        : result;
  }

  // Handles both Entry Data and Tabs
  private saveData(request: CubiqTS.ProfileUpdateRequestImpl): void {
    // Entry Data
    let savedOrderEntryData: CubiqTS.ProfileSettingImpl = this.getProfileSettingFromKey(
      PROFILE_KEY,
      request,
    );
    if (savedOrderEntryData == null) {
      // save as new
      let entrySettingObject: CubiqTS.ProfileSettingImpl = new CubiqTS.ProfileSettingImpl();
      entrySettingObject.profileSettingKey = PROFILE_KEY;
      entrySettingObject.profileSettingValue = this.generateEntryData();
      request.settings.push(entrySettingObject);
    } else {
      // save as update
      savedOrderEntryData.profileSettingValue = this.generateEntryData();
    }

    this.updateLoadedData();
  }
  public resetDataTest(): void {
    this.resetData();
  }

  // Handles both Entry Data and Tabs
  private resetData(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.control.isInitializing = true;
      // DATA
      // reset controls
      this.icClient.forEach((c) => c.reset());
      this.icAccount.forEach((c) => c.reset());
      this.icCcyPair.forEach((c) => c.resetInput());
      this.icAlgoType.forEach((c) => c.reset());
      this.icClientIntent.forEach((c) => c.resetInputControl());
      this.icFixedCcy.forEach((c) => c.reset());
      this.icOrderAmount.forEach((c) => c.resetInputControl());
      this.icGoodFrom.forEach((c) => c.reset());
      this.icGoodUntil.forEach((c) => c.reset());

      this.data.goodFrom.value = null;
      this.data.goodUntil.value = null;
      this.icPrivateOrder.forEach((c) => c.reset());
      this.data.client.accountConstraint = null;
      // re - initialize
      let activeUser: ActiveUser = this.parentComponent.activeUser;
      if (activeUser.profileSettings && activeUser.profileSettings.has(PROFILE_KEY)) {
        let savedData = activeUser.profileSettings.get(PROFILE_KEY);
        this.loadData(savedData);
      } else {
        this.control.isInitializing = false;
      }
      resolve();
    });
  }

  public generateEntryData(): string {
    let entryData = {
      client:
        this.data.client.value instanceof CubiqTS.ClientImpl
          ? this.data.client.value.clientId
          : null,
      account:
        this.data.account.value instanceof CubiqTS.AccountImpl ? this.data.account.value.id : null,
      ccyPair:
        typeof this.data.currencyPair.product === "string" &&
        this.data.currencyPair.product.length === 6
          ? this.data.currencyPair.product
          : null,
      algoType:
        this.data.algoType.value instanceof ICAutocompleteGenericModel
          ? this.data.algoType.value.value
          : null,
      clientIntent:
        this.control.showIntentAndAmount && this.data.clientIntent.value != null
          ? this.data.clientIntent.value
          : null,
      ccy:
        this.control.showIntentAndAmount &&
        typeof this.data.currencyPair.product === "string" &&
        this.data.fixedCcy.value instanceof ICFixedCcyModel
          ? this.data.fixedCcy.value.ccy
          : null,
      orderAmount:
        this.data.orderAmount.value instanceof CubiqTS.BigNumber
          ? this.data.orderAmount.value.toFixed(2)
          : null,
      goodFrom:
        this.data.goodFrom.value instanceof ICAutocompleteGenericModel
          ? this.data.goodFrom.value.value
          : null,
      goodUntil:
        this.data.goodUntil.value instanceof ICAutocompleteGenericModel
          ? this.data.goodUntil.value.value
          : null,
      privateOrder: this.data.privateOrder.value,
    };
    this.entryDataToUpdate = JSON.stringify(entryData);
    return this.entryDataToUpdate;
  }

  private loadData(savedData: any): void {
    try {
      this.cd.detach();
      let parsedData = JSON.parse(savedData);
      this.control.clientAccountConstraintAutoSelect = false;

      if (parsedData != null) {
        this.control.isLoading = true;
        this.control.isInitializing = false;
        let ccyPairSet: boolean = false;

        let promiseArray = new Array<Promise<any>>();
        // client (promise [0])
        if (parsedData.hasOwnProperty("client") && typeof parsedData.client === "string") {
          promiseArray.push(
            this.clientLookup
              .getActiveClient(parsedData.client, ClientAccountVisibilityType.Full)
              .catch((error) => {
                logger.out.error(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
                return null;
              }),
          );
        } else {
          // dummy promise to keep up appearances
          promiseArray.push(
            new Promise<null>((resolve) => {
              resolve(null);
            }),
          );
        }
        // account (promise [1])
        if (parsedData.hasOwnProperty("account") && typeof parsedData.account === "string") {
          promiseArray.push(
            this.accountLookup
              .getActiveAccount(parsedData.account, ClientAccountVisibilityType.Full)
              .catch((error) => {
                logger.out.error(`ERROR: _oeReverseAdapter.parseOrderDataImpl(): ${error}`);
                return null;
              }),
          );
        } else {
          promiseArray.push(
            new Promise<null>((resolve) => {
              resolve(null);
            }),
          );
        }
        // ccyPair
        if (
          parsedData.hasOwnProperty("ccyPair") &&
          typeof parsedData.ccyPair === "string" &&
          parsedData.ccyPair.length === 6
        ) {
          this.data.currencyPair.product = parsedData.ccyPair;
          this.icCcyPair.forEach((c) => c.setCcyPairExternally(parsedData.ccyPair));
          this.onCurrencyPairChange(null);
          ccyPairSet = true;
        }
        // algoType
        if (
          parsedData.hasOwnProperty("algoType") &&
          typeof parsedData.algoType === "string" &&
          this.data.algoType.dataset instanceof Array
        ) {
          let algoTypeModel = this.data.algoType.dataset.find(
            (item) => item.value === parsedData.algoType,
          );
          if (algoTypeModel instanceof ICAutocompleteGenericModel) {
            this.data.algoType.value = algoTypeModel;
            this.icAlgoType.forEach((c) => c.load(algoTypeModel));
          } else {
            logger.out.warn(
              `WARNING: algoDefaults.loadData(): Algo Type is present (${parsedData.algoType}) but the ICAutocompleteGenericModel model is not found!`,
            );
          }
        }
        // clientIntent
        if (
          parsedData.hasOwnProperty("clientIntent") &&
          typeof parsedData.clientIntent === "number" &&
          this.icClientIntent != null
        ) {
          this.icClientIntent.forEach((c) => c.writeValue(parsedData.clientIntent));
        }
        // ccy
        if (
          ccyPairSet &&
          parsedData.hasOwnProperty("ccy") &&
          typeof parsedData.ccy === "string" &&
          parsedData.ccy.length === 3
        ) {
          let componentModel = new ICFixedCcyModel(parsedData.ccy, parsedData.ccyPair);
          this.icFixedCcy.forEach((c) => c.load(componentModel, false));
          this.data.fixedCcy.ccyPair = parsedData.ccyPair;
          this.data.fixedCcy.fixedCcy = parsedData.ccy;
          if (this.data.fixedCcy.value == null) {
            this.data.fixedCcy.value = componentModel;
          }
          this.cd.detectChanges();
        } else if (
          ccyPairSet &&
          parsedData &&
          parsedData.ccy == null &&
          this.data.currencyPair != null &&
          this.data.currencyPair.base != null
        ) {
          let ccy = this.data.currencyPair.base;
          this.icFixedCcy.forEach((c) =>
            c.load(new ICFixedCcyModel(ccy, parsedData.ccyPair), false),
          );
          this.data.fixedCcy.ccyPair = parsedData.ccyPair;
          this.data.fixedCcy.fixedCcy = ccy;
          this.cd.detectChanges();
        }
        // orderAmount
        if (
          parsedData.hasOwnProperty("orderAmount") &&
          typeof parsedData.orderAmount === "string"
        ) {
          this.data.orderAmount.value = new CubiqTS.BigNumber(parsedData.orderAmount);
        }
        // goodFrom
        if (parsedData.hasOwnProperty("goodFrom") && typeof parsedData.goodFrom === "string") {
          let goodFromModel = this.data.goodFrom.allDataset.find(
            (item) => item.value === parsedData.goodFrom,
          );
          if (goodFromModel instanceof ICAutocompleteGenericModel) {
            this.data.goodFrom.value = goodFromModel;
          } else {
            logger.out.warn(
              `WARNING: algoDefaults.loadData(): Good From is present (${parsedData.goodFrom}) but the ICAutocompleteGenericModel model is not found in dataset!`,
            );
          }
        }
        // goodUntil
        if (parsedData.hasOwnProperty("goodUntil") && typeof parsedData.goodUntil === "string") {
          let goodUntilModel = this.data.goodUntil.allDataset.find(
            (item) => item.value === parsedData.goodUntil,
          );
          if (goodUntilModel instanceof ICAutocompleteGenericModel) {
            this.data.goodUntil.value = goodUntilModel;
          } else {
            logger.out.warn(
              `WARNING: algoDefaults.loadData(): Good Until is present (${parsedData.goodUntil}) but the ICAutocompleteGenericModel model is not found in dataset!`,
            );
          }
        }
        // privateOrder
        if (
          parsedData.hasOwnProperty("privateOrder") &&
          typeof parsedData.privateOrder === "boolean"
        ) {
          this.data.privateOrder.value = parsedData.privateOrder;
          this.icPrivateOrder.forEach((c) => c.load(parsedData.privateOrder));
        }

        Promise.all(promiseArray).then((values: Array<any>) => {
          // assign values
          let client = values[0];
          let account = values[1];

          // client
          if (client != null && client instanceof CubiqTS.ClientImpl) {
            this.data.client.value = client;
            this.icClient.forEach((c) => c.setDataValue(client, true, false, true, false));
          } else if (parsedData.hasOwnProperty("client") && typeof parsedData.client === "string") {
            logger.out.warn(
              `WARNING: algoDefaults.loadData(): Invalid client for '${parsedData.client}'`,
            );
          }
          setTimeout(
            () => {
              // load account after input_constrain & dataset is resolved
              if (account != null && account instanceof CubiqTS.AccountImpl) {
                this.data.account.value = account;
                this.icAccount.forEach((c) => c.setDataValue(account, true, false, true, false));
              } else if (
                parsedData.hasOwnProperty("account") &&
                typeof parsedData.account === "string"
              ) {
                logger.out.warn(
                  `WARNING: algoDefaults.loadData(): Invalid client for '${parsedData.client}'`,
                );
              }

              this.updateLoadedData();
              this.initializeMarketTimes();

              // end loading
              setTimeout(() => {
                this.control.isLoading = false;
                this.control.clientAccountConstraintAutoSelect = true;
                this.parentComponent.validate(this.categoryComponent, true, true).then(() => {
                  this.cd.detectChanges();
                });
              }, 100);
            },
            account == null ? 4 : 100,
          );
        });
      } else {
        this.control.isLoading = false;
        this.control.isInitializing = false;
        this.control.clientAccountConstraintAutoSelect = true;
      }
    } catch (ex) {
      // fallback
      this.control.isLoading = false;
      this.control.isInitializing = false;
      this.control.clientAccountConstraintAutoSelect = true;
    } finally {
      this.cd.detectChanges();
      this.cd.reattach();
    }
  }

  /**********************************************************************/
  /* VALUE CHANGE HANDLERS */

  // Client
  public onClientChange(dataFromInput: any): void {
    if (this.detectChanges(OENInputControl.CLIENT)) {
      this.pageDataChanged();
    }
  }

  public onClientSelect($event: any): void {
    if (this.detectChanges(OENInputControl.CLIENT)) {
      this.pageDataChanged();
    }
  }

  public onClientTabChange($event): void {
    if (this.detectChanges(OENInputControl.CLIENT_ACTIVE_TAB)) {
      this.pageDataChanged();
    }
  }
  // Account
  public onAccountChange(dataFromInput: any) {
    this.cd.detach();
    this.data.account.value = dataFromInput;
    if (this.detectChanges(OENInputControl.ACCOUNT)) {
      setTimeout(() => {
        this.data.client.accountConstraint = this.data.account.value;
      }, 0);
      this.pageDataChanged();
    }
    this.cd.detectChanges();
    this.cd.reattach();
  }

  public onAccountTabChange($event): void {
    if (this.detectChanges(OENInputControl.ACCOUNT_ACTIVE_TAB)) {
      this.pageDataChanged();
    }
  }
  // Ccy pair
  public onCurrencyPairChange($event: any): void {
    let productCode: string = this.data.currencyPair.product;
    let productCodeValid: boolean = false;

    if (typeof productCode === "string" && productCode.length === 6) {
      // validate
      productCodeValid =
        this.data.currencyPair.dataset.find(
          (item: string) => item.toLowerCase() === productCode.toLowerCase(),
        ) != null;
      if (productCodeValid) {
        // LOAD CCY MODEL
        let currencyPairLookup: CcyPairLookupService = this.currencyPairService.getBase();
        this.data.currencyPair.product = productCode;
        this.data.currencyPair.model = currencyPairLookup.getItem(productCode);
        this.data.currencyPair.inverted = false;
        // check for invert model
        if (this.data.currencyPair.model == null) {
          this.data.currencyPair.model = currencyPairLookup.getCcyPair(
            productCode.substr(3, 3),
            productCode.substr(0, 3),
          );
          this.data.currencyPair.inverted = this.data.currencyPair.model != null;
        }
        // ALLOCATE BASE / NON-BASE
        if (this.data.currencyPair.inverted) {
          this.data.currencyPair.base = productCode.substr(3, 3);
          this.data.currencyPair.nonBase = productCode.substr(0, 3);
        } else {
          this.data.currencyPair.base = productCode.substr(0, 3);
          this.data.currencyPair.nonBase = productCode.substr(3, 3);
        }

        // PROPAGATE FIXED CCY
        this.data.fixedCcy.ccyPair = productCode;
        this.cd.detectChanges();
      }
    }

    // ccyPair invalid or null
    if (!productCodeValid) {
      // RESET FIXED CCY
      this.icFixedCcy.forEach((c) => c.reset());
      this.data.fixedCcy.ccyPair = null;
      this.data.fixedCcy.fixedCcy = null;
      this.cd.detectChanges();
    }

    if (this.detectChanges(OENInputControl.CCY_PAIR)) {
      this.pageDataChanged();
    }
  }

  // Algo Type
  public onAlgoTypeChange($event: any) {
    if (this.detectChanges(OENInputControl.ALGO_TYPE)) {
      this.pageDataChanged();
    }
  }

  // Client Intent
  public onClientIntentToggleChange(dataFromInput: any) {
    if (this.detectChanges(OENInputControl.CLIENT_INTENT)) {
      this.pageDataChanged();
    }
  }

  // Fixed Ccy
  public onFixedCcyToggleChange(dataFromInput: any) {
    if (
      this.data.fixedCcy.value instanceof ICFixedCcyModel &&
      typeof this.data.fixedCcy.value.ccy === "string"
    ) {
      this.data.fixedCcy.fixedCcy = this.data.fixedCcy.value.ccy;
    } else {
      this.data.fixedCcy.fixedCcy = null;
    }

    if (this.detectChanges(OENInputControl.FIXED_CCY)) {
      this.pageDataChanged();
    }
  }

  // Order Amount
  public onOrderAmountChange(dataFromInput: any) {
    if (this.detectChanges(OENInputControl.ORDER_AMOUNT)) {
      this.pageDataChanged();
    }
  }

  public onOrderAmountBlur(dataFromInput: any) {
    if (this.detectChanges(OENInputControl.ORDER_AMOUNT)) {
      this.pageDataChanged();
    }
  }
  // Good From
  public onGoodFromChange($event) {
    if (this.detectChanges(OENInputControl.GOOD_FROM)) {
      this.pageDataChanged();
    } else {
      this.parentComponent.validate(this.categoryComponent);
    }
  }
  // Good Until
  public onGoodUntilChange($event) {
    if (this.detectChanges(OENInputControl.GOOD_UNTIL)) {
      this.pageDataChanged();
    } else {
      this.parentComponent.validate(this.categoryComponent);
    }
  }
  // Private Order
  public onPrivateOrderChange() {
    if (this.detectChanges(OENInputControl.PRIVATE_ORDER)) {
      this.pageDataChanged();
    }
  }

  public onUntilConstraintValidityChange(dataFromInput: any) {}

  /**********************************************************************/
  /* GOOD FROM - METHODS */

  public goodFromSuggestion(event: any): void {
    this.data.goodFrom.suggestions = [];
    for (let marketTime of this.data.goodFrom.validDataset) {
      if (marketTime.label.toLowerCase().indexOf(event.query.toLowerCase()) >= 0) {
        this.data.goodFrom.suggestions.push(marketTime);
      }
    }
  }

  /**********************************************************************/
  /* GOOD FROM - METHODS */

  public goodUntilSuggestion(event: any): void {
    this.data.goodUntil.suggestions = [];
    for (let marketTime of this.data.goodUntil.validDataset) {
      if (marketTime.label.toLowerCase().indexOf(event.query.toLowerCase()) >= 0) {
        this.data.goodUntil.suggestions.push(marketTime);
      }
    }
  }
}
